import axios, { AxiosRequestConfig } from 'axios';
import { NavigateFunction } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
if (!API_BASE_URL) throw new Error("API_BASE_URL is not defined");

interface RequestOptions {
  headers?: Record<string, string>;
  signal?: AbortSignal;
  params?: Record<string, any>;
  withCredentials?: boolean;
}

const bu_handleError = (error: any, navigate?: NavigateFunction) => {
  if (axios.isAxiosError(error)) {
    console.error("Axios error detected:", error.message);

    if (error.response) {
      console.warn(`Response status: ${error.response.status}`);

      if (error.response.status === 401) {
        console.warn("Unauthorized error detected. Redirecting to login...");
        sessionStorage.clear();

        if (navigate) {
          navigate("/login");
        }
      }

      throw new Error(error.response.data?.message || error.response.data?.error || "Request failed.");
    } else if (error.request) {
      throw new Error("No response from server.");
    }
  }

  throw new Error(error.message || "An unexpected error occurred.");
};

const handleError = (error: any, navigate?: NavigateFunction) => {
  if (axios.isAxiosError(error)) {
    console.error("Axios error detected:", error.message);

    if (error.response) {
      console.warn(`Response status: ${error.response.status}`);

      if (error.response.status === 401) {
        console.warn("Unauthorized error detected. Redirecting to login...");
        sessionStorage.clear();
        if (navigate) { navigate("/login"); }
      }

      // Instead of throwing a new Error, return the original error
      throw error; // Keep error.response intact
    } else if (error.request) {
      throw new Error("No response from server.");
    }
  }

  throw new Error(error.message || "An unexpected error occurred.");
};


export const apiService = {
  post: async (endpoint: string, data: any, options: RequestOptions = {}, navigate?: NavigateFunction) => {
    try {
      const response = await axios.post(`${API_BASE_URL}${endpoint}`, data, {
        headers: { 'Content-Type': 'application/json', ...options.headers },
        signal: options.signal,
        params: options.params,
        withCredentials: true,
      });

      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  },

  get: async (endpoint: string, options: RequestOptions = {}, navigate?: NavigateFunction) => {
    try {
      const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
        headers: { 'Content-Type': 'application/json', ...options.headers },
        signal: options.signal,
        params: options.params,
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      handleError(error, navigate);
      throw error;
    }
  },

  put: async (endpoint: string, data: any, options: RequestOptions = {}, navigate?: NavigateFunction) => {
    try {
      const response = await axios.put(`${API_BASE_URL}${endpoint}`, data, {
        headers: { 'Content-Type': 'application/json', ...options.headers },
        signal: options.signal,
        params: options.params,
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  },

  delete: async (endpoint: string, options: RequestOptions = {}, navigate?: NavigateFunction) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}${endpoint}`, {
        headers: { 'Content-Type': 'application/json', ...options.headers },
        signal: options.signal,
        params: options.params,
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  },

  patch: async (endpoint: string, data: any, options: RequestOptions = {}, navigate?: NavigateFunction) => {
    try {
      const response = await axios.patch(`${API_BASE_URL}${endpoint}`, data, {
        headers: { 'Content-Type': 'application/json', ...options.headers },
        signal: options.signal,
        params: options.params,
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  },

  isCancel: (error: any) => axios.isCancel(error),
};

axios.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('token');
  if (token) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(res => res, async err => {
  const originalRequest = err.config;

  // Only attempt token refresh if the original request had an Authorization header
  //const hasAuthHeader = originalRequest.headers?.['Authorization'];
  const hasToken = sessionStorage.getItem('token');

  // If the failing request is /users/refresh-token, don't retry
  if (originalRequest?.url?.includes('/users/refresh-token')) {
    return Promise.reject(err);
  }

  // && hasAuthHeader
  if (err.response?.status === 401 && !originalRequest._retry && hasToken) {
    originalRequest._retry = true;
    originalRequest.headers = originalRequest.headers || {};

    try {
      const res = await axios.post(`${API_BASE_URL}/users/refresh-token`, {}, {
        withCredentials: true
      });

      const newToken = res.data.token;
      sessionStorage.setItem('token', newToken);

      originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
      return axios(originalRequest);
    } catch (refreshErr) {
      sessionStorage.clear();
      window.location.href = '/login';
    }
  }

  return Promise.reject(err);
}
);